import React from "react";
import { Link } from "react-router-dom";
import {
  FileText, Download, ExternalLink, ShieldCheck, Award, Building2, Users, ArrowRight,
  Scale, BookOpen, IdCard, FileBadge2, ArrowLeft, Sparkles, CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { LOGO_URL } from "../data/mock";

const ESSE_LINK = "https://server.esserete.it/formazionescheda2.html?asn=B0196";
const MIMIT_LINK = "https://www.mimit.gov.it/";
const ESSE_LOGO = "https://customer-assets.emergentagent.com/job_acdt-hub/artifacts/iau77wq9_ESSE%20STUDI.jpeg";

const DOCUMENTI_DIRETTIVO = [
  { id: "statuto", nome: "Statuto A.C.D.T.", desc: "Documento costitutivo e regolamento ufficiale dell'associazione", tipo: "PDF", size: "248 KB", icon: Scale, file: "/documenti/statuto-acdt.pdf" },
  { id: "atto", nome: "Atto Costitutivo", desc: "Atto di costituzione dell'associazione A.C.D.T.", tipo: "PDF", size: "180 KB", icon: FileText, file: "/documenti/atto-costitutivo.pdf" },
  { id: "deont", nome: "Codice Deontologico", desc: "Principi etici e regole di condotta per i soci A.C.D.T.", tipo: "PDF", size: "195 KB", icon: BookOpen, file: "/documenti/codice-deontologico.pdf" },
  { id: "condotta", nome: "Codice di Condotta", desc: "Norme di comportamento professionale per i consulenti", tipo: "PDF", size: "165 KB", icon: ShieldCheck, file: "/documenti/codice-condotta.pdf" },
  { id: "registro", nome: "Regolamento Registro Nazionale", desc: "Modalità di iscrizione al Registro Nazionale dei Consulenti", tipo: "PDF", size: "210 KB", icon: FileBadge2, file: "/documenti/regolamento-registro.pdf" },
  { id: "cfdt", nome: "Regolamento CFDT", desc: "Sistema dei Crediti Formativi Digitali Tecnologici", tipo: "PDF", size: "175 KB", icon: Award, file: "/documenti/regolamento-cfdt.pdf" },
  { id: "privacy", nome: "Informativa Privacy GDPR", desc: "Trattamento dei dati personali ai sensi del Reg. UE 2016/679", tipo: "PDF", size: "120 KB", icon: ShieldCheck, file: "/documenti/privacy-gdpr.pdf" },
];

const DIRETTIVO = [
  { ruolo: "Presidente", nome: "Davide Campanella", ini: "DC", photo: "https://digitaltechsolutions.it/wp-content/uploads/2026/03/team-campanella.jpg" },
  { ruolo: "Vice Presidente", nome: "Giovanni Sapuppo", ini: "GS", photo: "https://digitaltechsolutions.it/wp-content/uploads/2026/03/team-sapuppo.jpg" },
  { ruolo: "Segretario", nome: "Michele Roccaro", ini: "MR", photo: "https://digitaltechsolutions.it/wp-content/uploads/2026/03/team-roccaro.jpg" },
  { ruolo: "Consigliere", nome: "Simona Baracico", ini: "SB", photo: "https://digitaltechsolutions.it/wp-content/uploads/2026/03/team-barracico.jpg" },
  { ruolo: "Consigliere", nome: "Luca Milardi", ini: "LM", photo: "https://digitaltechsolutions.it/wp-content/uploads/2026/03/team-minardi.jpg" },
];

export default function DirettivoDocumentazione() {
  return (
    <>
      <SEO
        title="Direttivo & Documentazione - A.C.D.T."
        description="Direttivo, statuto, codici e documenti ufficiali dell'Associazione Consulenti Digitali Tecnologici. Aggregazione MIMIT tramite Centro Studi Esse."
        path="/direttivo-documentazione"
      />
      <Navbar />

      <main className="min-h-screen bg-gradient-to-b from-[#f5f7ff] via-white to-[#f5f7ff]">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#f59e0b]/25 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-[#3b82f6]/25 blur-3xl" />
          <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
            <Link to="/" className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-[#fbbf24] hover:underline">
              <ArrowLeft className="h-4 w-4" /> Torna alla home
            </Link>
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#fbbf24]">
              <Building2 className="h-3.5 w-3.5" /> Documentazione ufficiale
            </div>
            <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
              Direttivo &<br />
              <span className="text-[#fbbf24]">Documentazione</span>
            </h1>
            <p className="mt-5 max-w-2xl text-[15px] md:text-[16.5px] text-slate-200 leading-relaxed">
              Tutti i documenti ufficiali dell&apos;Associazione Consulenti Digitali Tecnologici: statuto, codici, regolamenti e
              dati istituzionali. Scarica i PDF o consulta la <strong className="text-white">scheda tecnica completa</strong> sul
              portale del Centro Studi Esse.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#documenti" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold transition-colors">
                <Download className="h-4 w-4" /> Vai ai documenti
              </a>
              <a href={ESSE_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold transition-colors">
                Scheda MIMIT Centro Esse <ExternalLink className="h-4 w-4" />
              </a>
            </div>

            {/* Mini info */}
            <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-2xl">
              <InfoBox label="Codice ASN" value="B0196" />
              <InfoBox label="Aggregazione" value="MIMIT" />
              <InfoBox label="Tramite" value="Centro Studi Esse" />
            </div>
          </div>
        </section>

        {/* DIRETTIVO */}
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#102a6b]/8 text-[#102a6b] text-[11px] font-extrabold uppercase tracking-[0.2em]">
              <Users className="h-3.5 w-3.5" /> Consiglio direttivo
            </div>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-[#0a1f55]">Il Direttivo A.C.D.T.</h2>
            <p className="mt-3 text-[14.5px] text-slate-600">Le persone che guidano l&apos;associazione e ne curano le attività istituzionali.</p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {DIRETTIVO.map((d) => (
              <div key={d.nome} className="rounded-2xl bg-white border border-slate-200 p-5 text-center hover:border-[#102a6b]/40 hover:shadow-md transition-all" data-testid={`direttivo-${d.nome.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="relative mx-auto h-32 w-32 rounded-2xl overflow-hidden bg-gradient-to-br from-[#102a6b] to-[#1e40af]">
                  {d.photo ? (
                    <img
                      src={d.photo}
                      alt={d.nome}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fb = e.currentTarget.nextElementSibling;
                        if (fb) fb.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div className="absolute inset-0 flex items-center justify-center text-white text-2xl font-extrabold" style={{ display: d.photo ? "none" : "flex" }}>
                    {d.ini}
                  </div>
                </div>
                <div className="mt-4 text-[10.5px] uppercase tracking-wider font-extrabold text-[#b45309]">{d.ruolo}</div>
                <div className="mt-1 font-extrabold text-[#0a1f55]">{d.nome}</div>
              </div>
            ))}
          </div>
        </section>

        {/* DOCUMENTI */}
        <section id="documenti" className="bg-white border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[11px] font-extrabold uppercase tracking-[0.2em]">
                  <FileText className="h-3.5 w-3.5" /> Download disponibili
                </div>
                <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-[#0a1f55]">Documenti ufficiali</h2>
                <p className="mt-2 text-[14.5px] text-slate-600 max-w-2xl">Tutti i documenti istituzionali dell&apos;A.C.D.T. sono disponibili per la consultazione e il download libero.</p>
              </div>
              <div className="text-[11.5px] text-slate-500 bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-lg">
                <Sparkles className="inline h-3.5 w-3.5 text-[#f59e0b] mr-1" />
                <strong className="text-[#0a1f55]">{DOCUMENTI_DIRETTIVO.length} documenti</strong> disponibili
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {DOCUMENTI_DIRETTIVO.map((d) => {
                const I = d.icon;
                return (
                  <div key={d.id} data-testid={`doc-${d.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#102a6b]/40 hover:shadow-md transition-all flex flex-col">
                    <div className="flex items-start gap-3">
                      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#102a6b] to-[#1e40af] text-white flex items-center justify-center flex-shrink-0">
                        <I className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-extrabold text-[#0a1f55] leading-tight">{d.nome}</h3>
                        <p className="text-[12.5px] text-slate-600 mt-1 line-clamp-2 min-h-[2.6rem]">{d.desc}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                      <div className="text-[11.5px] text-slate-500">
                        <span className="font-bold text-[#102a6b]">{d.tipo}</span> • {d.size}
                      </div>
                      <a href={d.file} download className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[#102a6b] hover:bg-[#0a1f55] text-white text-[12px] font-bold transition-colors">
                        <Download className="h-3.5 w-3.5" /> Scarica
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* AGGREGAZIONE MIMIT - SEZIONE EVIDENZIATA */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a]" />
          <div className="absolute -top-20 right-1/4 h-80 w-80 rounded-full bg-[#f59e0b]/20 blur-3xl" />
          <div className="absolute -bottom-20 left-1/4 h-80 w-80 rounded-full bg-[#3b82f6]/20 blur-3xl" />

          <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
            <div className="grid lg:grid-cols-5 gap-10 items-center">
              <div className="lg:col-span-3 text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f59e0b] text-[#0a1020] text-[11px] font-extrabold uppercase tracking-[0.2em] mb-5">
                  <ShieldCheck className="h-3.5 w-3.5" /> Riconoscimento ufficiale
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.05]">
                  Aggregazione <span className="text-[#fbbf24]">MIMIT</span><br />
                  di A.C.D.T.
                </h2>
                <p className="mt-5 text-[15px] md:text-[16px] text-slate-200 leading-relaxed max-w-xl">
                  L&apos;Associazione Consulenti Digitali Tecnologici è riconosciuta come{" "}
                  <strong className="text-white">soggetto aggregato presso il MIMIT</strong>{" "}
                  (Ministero delle Imprese e del Made in Italy) <strong className="text-[#fbbf24]">grazie al Centro Studi Esse</strong>,
                  ente di formazione accreditato che ne valida la conformità ai parametri ministeriali.
                </p>

                <div className="mt-7 space-y-3">
                  {[
                    "Iscrizione validata presso il Ministero delle Imprese e del Made in Italy",
                    "Aggregazione gestita dal Centro Studi Esse (codice ASN B0196)",
                    "Documenti ufficiali consultabili e scaricabili dalla scheda tecnica",
                  ].map((t, i) => (
                    <div key={i} className="flex items-start gap-3 text-[13.5px] text-slate-200">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={ESSE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="btn-esse-mimit"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold text-[14px] transition-all hover:scale-[1.02] shadow-xl shadow-[#f59e0b]/30"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Apri scheda tecnica Centro Esse Studi
                  </a>
                  <a
                    href={MIMIT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold text-[14px] transition-colors"
                  >
                    Sito ufficiale MIMIT <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>

              {/* Card Centro Studi Esse */}
              <div className="lg:col-span-2">
                <div className="rounded-3xl bg-white p-7 md:p-8 shadow-2xl">
                  <div className="flex items-center gap-4 pb-6 border-b border-slate-200">
                    <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#102a6b] to-[#1e40af] text-white flex items-center justify-center flex-shrink-0">
                      <img
                        src={ESSE_LOGO}
                        alt="Centro Studi Esse"
                        className="h-12 w-12 object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <span className="hidden h-12 w-12 items-center justify-center font-extrabold text-2xl">E</span>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#b45309]">Partner istituzionale</div>
                      <div className="text-xl font-extrabold text-[#0a1f55] leading-tight">Centro Studi Esse</div>
                    </div>
                  </div>

                  <div className="mt-5 space-y-3 text-[13.5px] text-slate-700">
                    <div className="flex items-start gap-2">
                      <Building2 className="h-4 w-4 text-[#102a6b] mt-0.5 flex-shrink-0" />
                      <span>Ente di formazione professionale accreditato</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Award className="h-4 w-4 text-[#102a6b] mt-0.5 flex-shrink-0" />
                      <span>Validatore aggregazione MIMIT</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <IdCard className="h-4 w-4 text-[#102a6b] mt-0.5 flex-shrink-0" />
                      <span>Codice ASN: <strong className="text-[#0a1f55]">B0196</strong></span>
                    </div>
                  </div>

                  <a
                    href={ESSE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold text-[13.5px] transition-colors"
                  >
                    Vedi documenti su Esse <ExternalLink className="h-4 w-4" />
                  </a>
                  <p className="mt-3 text-[11px] text-slate-500 text-center">
                    Verrai reindirizzato al portale ufficiale del Centro Studi Esse
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA FINALE - Pulsante grande verso Esse */}
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="rounded-3xl bg-gradient-to-br from-white to-[#fff7ed] border-2 border-[#f59e0b]/30 p-8 md:p-12 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[11px] font-extrabold uppercase tracking-[0.2em]">
              <ExternalLink className="h-3.5 w-3.5" /> Documenti completi
            </div>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-[#0a1f55] max-w-2xl mx-auto leading-tight">
              Tutti i documenti ufficiali sulla scheda tecnica MIMIT
            </h2>
            <p className="mt-4 text-[15px] text-slate-600 max-w-xl mx-auto">
              Accedi al portale del Centro Studi Esse per consultare la scheda tecnica completa di A.C.D.T.
              (codice ASN <strong className="text-[#0a1f55]">B0196</strong>) con tutti gli allegati ministeriali.
            </p>
            <a
              href={ESSE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-esse-bottom"
              className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-[#102a6b] hover:bg-[#0a1f55] text-white font-extrabold text-[15px] transition-all hover:scale-[1.02] shadow-xl shadow-[#102a6b]/30"
            >
              <ExternalLink className="h-5 w-5" />
              Apri scheda Centro Esse Studi - ASN B0196
              <ArrowRight className="h-5 w-5" />
            </a>
            <p className="mt-4 text-[11px] text-slate-500">
              Aggregazione MIMIT di A.C.D.T. <strong className="text-[#b45309]">grazie al Centro Studi Esse</strong>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-[#fbbf24]">{label}</div>
      <div className="mt-0.5 text-[15px] font-extrabold text-white">{value}</div>
    </div>
  );
}
